"use client";

import { useState, useEffect } from "react";
import { ShoppingBag, Minus, Plus, Check, AlertCircle, Sparkles, Heart, Clock } from "lucide-react";
import { useCartStore } from "@/lib/store/useCartStore";
import WishlistButton from "@/components/store/WishlistButton";
import { useSelectedVariantImage } from "@/contexts/SelectedVariantContext";
import { getEffectivePrice } from "@/lib/pricing";

function parseImages(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

export default function ProductDetailClient({ product }: { product: any }) {
  const addItem = useCartStore((state) => state.addItem);
  const { setImages: setGalleryImages } = useSelectedVariantImage();
  const [selectedVariant, setSelectedVariant] = useState(
    product.variants.find((v: any) => v.isDefault) || product.variants[0]
  );

  // Seçili varyantın kendine ait görselleri varsa galeriye bildir (ilk yüklemede de)
  useEffect(() => {
    setGalleryImages(parseImages(selectedVariant?.images));
  }, [selectedVariant, setGalleryImages]);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [shaking, setShaking] = useState(false);

  const [localCartCount, setLocalCartCount] = useState(product.cartCount || 0);
  const [localFavoriteCount, setLocalFavoriteCount] = useState(product.favoriteCount || 0);

  const stock: number = selectedVariant.stock ?? 0;
  const atMax = quantity >= stock;
  const atMin = quantity <= 1;

  const { hasDiscount, effectivePrice, discountPercent, daysLeft } = getEffectivePrice(selectedVariant);

  const productImages: string[] = parseImages(product.images);
  const resolveImage = () => parseImages(selectedVariant.images)[0] || productImages[0] || null;

  const handleVariantChange = (variant: any) => {
    setSelectedVariant(variant);
    setQuantity(1); // varyasyon değişince adeti sıfırla
  };

  const handleIncrease = () => {
    if (quantity >= stock) {
      setShaking(true);
      setTimeout(() => setShaking(false), 400);
      return;
    }
    setQuantity(q => q + 1);
  };

  const handleDecrease = () => {
    setQuantity(q => Math.max(1, q - 1));
  };

  const handleAddToCart = async () => {
    if (stock <= 0) return;
    addItem({
      id: selectedVariant.id,
      productId: product.id,
      name: product.nameTR,
      mlLabel: selectedVariant.mlLabel,
      price: effectivePrice,
      quantity,
      slug: product.slug,
      image: resolveImage(),
      campaign: product.campaign ? (typeof product.campaign === "string" ? JSON.parse(product.campaign) : product.campaign) : null,
      kargoUcreti: selectedVariant.kargoUcreti ?? null,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);

    // UI Feedback: Yerel sayacı artır (API çağrısı artık Store içinde merkezi yapılıyor)
    setLocalCartCount((prev: number) => prev + 1);
  };

  const handleFavoriteToggle = async (isFav: boolean) => {
    // UI Feedback: Yerel sayacı güncelle (API çağrısı artık Store içinde merkezi yapılıyor)
    setLocalFavoriteCount((prev: number) => isFav ? prev + 1 : Math.max(0, prev - 1));
  };

  const campaign = product.campaign ? (typeof product.campaign === "string" ? JSON.parse(product.campaign) : product.campaign) : null;

  return (
    <div className="space-y-10">
      {/* Kampanya Bilgisi */}
      {campaign?.isActive && (
        <div className="animate-in fade-in slide-in-from-top-4 duration-700">
          {campaign.type === "TIERED" && campaign.tiers.length > 0 && (
            <div className="bg-[#C9A84C]/5 border border-[rgba(201,168,76,0.15)] rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-[#C9A84C]">
                <Sparkles size={16} />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Özel Fırsat</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {campaign.tiers.sort((a: any, b: any) => a.minQuantity - b.minQuantity).map((tier: any, i: number) => (
                  <div key={i} className="bg-black/40 border border-white/5 px-3 py-2 rounded-lg text-xs">
                    <span className="text-[#F4F0E8] font-medium">{tier.minQuantity} Adet Alımda</span>
                    <div className="text-[#C9A84C] font-bold mt-0.5">
                      1 Tanesi {tier.discountPercent === 100 ? "BEDAVA!" : `%${tier.discountPercent} İNDİRMLİ`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {campaign.type === "GIFT" && campaign.giftNote && (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center text-purple-400">
                <ShoppingBag size={20} />
              </div>
              <div>
                <p className="text-[10px] text-purple-400 font-bold uppercase tracking-widest">Hediye Kampanyası</p>
                <p className="text-sm text-[#F4F0E8] font-serif mt-0.5">{campaign.giftNote}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Varyasyon Seçimi */}
      <div className="space-y-4">
        <label className="text-xs text-[#7A7670] uppercase tracking-widest font-medium">Boyut Seçimi</label>
        <div className="flex flex-wrap gap-3">
          {product.variants.map((variant: any) => (
            <button
              key={variant.id}
              onClick={() => handleVariantChange(variant)}
              className={`px-6 py-3 border transition-all duration-300 text-sm tracking-widest uppercase ${
                selectedVariant.id === variant.id
                  ? "border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/5"
                  : "border-[rgba(201,168,76,0.12)] text-[#7A7670] hover:border-[#C9A84C]/50"
              }`}
            >
              {variant.mlLabel}
            </button>
          ))}
        </div>
      </div>

      {/* Popülerlik Rozetleri (Sosyal Kanıt) */}
      <div className="flex flex-wrap gap-4 min-h-[40px]">
        {(localCartCount > 0) ? (
          <div className="flex items-center gap-3 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-xl px-5 py-2.5 animate-in zoom-in duration-500">
            <div className="relative">
              <ShoppingBag size={16} className="text-[#C9A84C]" />
              <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-[#C9A84C] rounded-full animate-ping" />
              <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-[#C9A84C] rounded-full" />
            </div>
            <span className="text-[11px] text-[#F4F0E8] font-bold uppercase tracking-[0.1em]">
              {localCartCount} Kişi Sepetine Ekledi
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-5 py-2.5 opacity-60">
            <ShoppingBag size={16} className="text-[#7A7670]" />
            <span className="text-[10px] text-[#7A7670] font-medium uppercase tracking-wider">İlk ekleyen sen ol!</span>
          </div>
        )}

        {(localFavoriteCount > 0) ? (
          <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-2.5 animate-in zoom-in duration-700">
            <Heart size={16} className="text-red-500 fill-red-500" />
            <span className="text-[11px] text-[#F4F0E8] font-bold uppercase tracking-[0.1em]">
              {localFavoriteCount} Beğeni
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-5 py-2.5 opacity-60">
            <Heart size={16} className="text-[#7A7670]" />
            <span className="text-[10px] text-[#7A7670] font-medium uppercase tracking-wider">Henüz beğeni yok</span>
          </div>
        )}
      </div>

      {/* Fiyat ve Adet */}
      <div className="flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[rgba(201,168,76,0.08)]">
        <div className="space-y-1">
          <div className="text-[10px] text-[#7A7670] uppercase tracking-widest">Fiyat</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-serif text-4xl text-[#C9A84C] font-light">
              ₺{effectivePrice.toLocaleString('tr-TR')}
            </span>
            {hasDiscount && (
              <>
                <span className="text-lg text-[#7A7670] line-through font-light opacity-50">
                  ₺{selectedVariant.price.toLocaleString('tr-TR')}
                </span>
                <span className="bg-gradient-to-r from-[#C9A84C] to-[#D4B664] text-black text-xs font-bold px-2.5 py-1 rounded-full shadow-lg shadow-[#C9A84C]/20">
                  %{discountPercent} İNDİRİM
                </span>
              </>
            )}
          </div>
          {hasDiscount && daysLeft != null && (
            <div className="flex items-center gap-1.5 text-[10px] text-orange-400 font-medium uppercase tracking-widest mt-1">
              <Clock size={11} />
              {daysLeft > 0 ? `Bu fiyat ${daysLeft} gün daha geçerli` : "Bu fiyatın son günü"}
            </div>
          )}
        </div>

        {/* Adet Kontrolü */}
        <div className="space-y-2">
          <div className="flex items-center gap-4 bg-[#111114] border border-[rgba(201,168,76,0.12)] p-1 rounded">
            <button
              onClick={handleDecrease}
              disabled={atMin}
              className="w-10 h-10 flex items-center justify-center text-[#7A7670] hover:text-[#C9A84C] transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Minus size={16} />
            </button>
            <span className="w-10 text-center text-[#F4F0E8] font-mono text-lg select-none">
              {quantity}
            </span>
            <button
              onClick={handleIncrease}
              disabled={stock <= 0}
              className={`w-10 h-10 flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed ${
                atMax ? "text-red-400 cursor-not-allowed" : "text-[#7A7670] hover:text-[#C9A84C]"
              } ${shaking ? "animate-shake" : ""}`}
            >
              <Plus size={16} />
            </button>
          </div>
          {atMax && stock > 0 && (
            <div className="flex items-center gap-1.5 text-orange-400">
              <AlertCircle size={12} />
              <span className="text-[10px] uppercase tracking-wider">Maksimum stok: {stock} adet</span>
            </div>
          )}
        </div>
      </div>

      {/* İşlem Butonları */}
      <div className="flex gap-4">
        <button
          onClick={handleAddToCart}
          disabled={stock <= 0 || added}
          className={`flex-[2] py-5 flex items-center justify-center gap-3 font-bold uppercase tracking-[0.2em] text-xs transition-all disabled:opacity-80 disabled:cursor-not-allowed group ${
            added ? "bg-green-600 text-white" : "bg-[#C9A84C] text-black hover:bg-[#F4F0E8]"
          }`}
        >
          {added ? <Check size={18} /> : <ShoppingBag size={18} className="group-hover:scale-110 transition-transform" />}
          {added ? "Sepete Eklendi" : stock > 0 ? "Sepete Ekle" : "Stokta Yok"}
        </button>

        <WishlistButton
          variant="full"
          onToggle={handleFavoriteToggle}
          product={{
            id: product.id,
            name: product.nameTR,
            price: effectivePrice,
            slug: product.slug
          }}
        />
      </div>

      {/* Stok Durumu */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${stock > 10 ? "bg-green-500" : stock > 0 ? "bg-orange-500" : "bg-red-500"}`} />
          <span className="text-[10px] text-[#7A7670] uppercase tracking-widest font-medium">
            {stock > 0 ? "Stok Durumu" : "Tükendi"}
          </span>
        </div>
        {stock > 0 && stock <= 5 && (
          <div className="bg-red-500/10 border border-red-500/20 px-3 py-2 rounded flex items-center gap-2 text-red-400 animate-pulse">
            <AlertCircle size={14} />
            <span className="text-[11px] font-bold uppercase tracking-wider">Acele Et! Son {stock} adet kaldı.</span>
          </div>
        )}
        {stock > 5 && stock <= 15 && (
          <div className="text-[10px] text-orange-400 font-medium uppercase tracking-widest">
            Sınırlı Stok: {stock} adet kaldı
          </div>
        )}
        {stock > 15 && (
          <div className="text-[10px] text-[#7A7670] uppercase tracking-widest">
            Stokta Mevcut ({stock} adet)
          </div>
        )}
      </div>
    </div>
  );
}
